import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CalculatorShell, { Field, Result, inputClass } from "@/components/CalculatorShell";
import { getCalculator } from "@/data/calculators";
import { redundancyPackage, gbp, longDate, TERMINATION_TAX_FREE } from "@/lib/employment-calcs";

const calc = getCalculator("redundancy-package-tax-calculator");

const MONEY_FIELDS = [
  { k: "statutoryRedundancy", label: "Statutory redundancy pay (£)", hint: "Use our redundancy pay calculator if you don't have the figure." },
  { k: "enhancedRedundancy", label: "Enhanced redundancy pay on top (£)", hint: "Paid under a redundancy scheme solely because of redundancy." },
  { k: "otherCompensation", label: "Other non-contractual compensation (£)", hint: "For example an ex gratia or settlement payment for losing the job." },
  { k: "pilon", label: "Pay in lieu of notice under the contract (£)", hint: "Or any other notice pay for notice not worked." },
  { k: "finalPay", label: "Final salary, including notice worked (£)" },
  { k: "holidayPay", label: "Holiday pay for untaken leave (£)" },
  { k: "otherEarnings", label: "Bonus, commission or other earnings (£)" },
];

export default function RedundancyPackageCalculator() {
  const [form, setForm] = useState({
    statutoryRedundancy: "",
    enhancedRedundancy: "",
    otherCompensation: "",
    pilon: "",
    finalPay: "",
    holidayPay: "",
    otherEarnings: "",
    noticeGivenDate: "",
    lastDay: "",
    minNoticeAmount: "",
    minNoticeUnit: "weeks",
    payFrequency: "monthly",
    daysInPayPeriod: "",
    basicPayLastPeriod: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ready = form.lastDay && form.minNoticeAmount !== "" && form.basicPayLastPeriod !== "";
  const r = useMemo(() => (ready ? redundancyPackage(form) : null), [form, ready]);

  const notes = [];
  if (r && !r.error) {
    if (r.penp > 0) {
      notes.push(
        `${gbp(r.penp)} of the non-contractual payments is post-employment notice pay: basic pay for the ${r.rule === "months" ? `${r.D} month${r.D === 1 ? "" : "s"}` : `${r.unworkedDays} days`} of minimum notice not worked or paid as notice pay, up to ${longDate(r.earliestLawfulTermination)}. It is taxed as earnings, not covered by the £30,000.`,
      );
    } else if (r.unworkedDays > 0) {
      notes.push("The notice pay already paid covers the unworked minimum notice, so no post-employment notice pay is carved out.");
    }
    if (r.taxableExcess > 0) {
      notes.push(`${gbp(r.taxableExcess)} is over the £30,000 threshold. The employee pays income tax on it but no National Insurance; the employer pays Class 1A National Insurance of ${Math.round(r.class1ARate * 100)}% (${gbp(r.employerClass1A)}) through payroll.`);
    }
    notes.push("How much income tax is actually deducted depends on the employee's tax code and other income in the tax year. Check the figures with your payroll provider or accountant before you pay.");
    notes.push("Not covered: payments that are really a reward for work (HMRC treats those as earnings), pension contributions, benefits in kind, foreign service relief and payments on death or disability.");
  }

  return (
    <CalculatorShell
      calc={calc}
      howItWorks={
        <>
          <p>A leaving package is taxed in three different ways.</p>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>Taxed as earnings:</strong> final salary, notice pay including pay in lieu of notice, holiday pay,
              bonuses, and any post-employment notice pay. Income tax and National Insurance apply as normal.
            </li>
            <li>
              <strong>Tax-free up to £30,000:</strong> statutory redundancy pay, redundancy pay paid under a scheme solely
              because of redundancy, and other non-contractual compensation for losing the job (Income Tax (Earnings and
              Pensions) Act 2003, ss309, 401 and 403; HMRC Statement of Practice 1/94).
            </li>
            <li>
              <strong>Over £30,000:</strong> the excess is subject to income tax but not employee National Insurance. The
              employer pays Class 1A National Insurance on it at 15%.
            </li>
          </ul>
          <p>
            <strong>Post-employment notice pay</strong> stops employers dressing up notice pay as a tax-free payment. Where
            the employee does not work their full minimum notice, the basic pay for the unworked part is taken out of the
            non-contractual payments and taxed as earnings, less any notice pay already taxed (ITEPA 2003, s402D). Minimum
            notice is the notice the employer must give under the contract or the law, whichever is longer, counted from
            the day notice was given (s402E). Statutory redundancy pay is never converted, and the amount converted cannot
            be more than the non-contractual payments themselves.
          </p>
          <p>
            Need the statutory figure first? Use our{" "}
            <Link to="/calculators/redundancy-pay-calculator" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">redundancy pay calculator</Link>,
            and our <Link to="/calculators/notice-period-calculator" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">notice period calculator</Link>{" "}
            for the statutory minimum notice.
          </p>
        </>
      }
      example={
        <p>
          Sam is made redundant on 30 November 2026 with no notice worked. His contract gives three months' notice and he
          earns £3,000 a month. He gets £10,000 statutory redundancy pay and £25,000 enhanced redundancy pay, but there is
          no pay in lieu clause and no notice pay. Three months' basic pay, <strong>£9,000</strong>, is post-employment notice
          pay and taxed as earnings. The remaining £16,000 plus the £10,000 statutory payment is £26,000, all within the
          £30,000 and <strong>tax-free</strong>.
        </p>
      }
    >
      <div className="grid gap-6 sm:grid-cols-2">
        {MONEY_FIELDS.map((f) => (
          <Field key={f.k} label={f.label} hint={f.hint} htmlFor={`pkg-${f.k}`}>
            <input id={`pkg-${f.k}`} type="number" min="0" step="0.01" inputMode="decimal" className={inputClass} value={form[f.k]} onChange={set(f.k)} data-testid={`pkg-${f.k}`} />
          </Field>
        ))}
      </div>

      <p className="mt-10 eyebrow text-ink/50">Notice and basic pay</p>
      <div className="mt-4 grid gap-6 sm:grid-cols-2">
        <Field label="Date notice was given" hint="Leave blank if no notice was given." htmlFor="pkg-notice">
          <input id="pkg-notice" type="date" className={inputClass} value={form.noticeGivenDate} onChange={set("noticeGivenDate")} data-testid="pkg-notice" />
        </Field>
        <Field label="Last day of employment" htmlFor="pkg-last">
          <input id="pkg-last" type="date" className={inputClass} value={form.lastDay} onChange={set("lastDay")} data-testid="pkg-last" />
        </Field>
        <Field label="Minimum notice the employer must give" hint="The longer of the contract and the statutory minimum." htmlFor="pkg-min">
          <div className="flex gap-2">
            <input id="pkg-min" type="number" min="0" step="1" inputMode="numeric" className={inputClass} value={form.minNoticeAmount} onChange={set("minNoticeAmount")} data-testid="pkg-min" />
            <select aria-label="Unit" className={`${inputClass} max-w-[9rem]`} value={form.minNoticeUnit} onChange={set("minNoticeUnit")} data-testid="pkg-min-unit">
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
          </div>
        </Field>
        <Field label="How often is basic pay paid?" htmlFor="pkg-freq">
          <select id="pkg-freq" className={inputClass} value={form.payFrequency} onChange={set("payFrequency")} data-testid="pkg-freq">
            <option value="monthly">Monthly, in equal amounts</option>
            <option value="weekly">Weekly</option>
            <option value="fourweekly">Every four weeks</option>
            <option value="other">Another period</option>
          </select>
        </Field>
        {form.payFrequency === "other" && (
          <Field label="Days in the last pay period" htmlFor="pkg-days">
            <input id="pkg-days" type="number" min="1" step="1" inputMode="numeric" className={inputClass} value={form.daysInPayPeriod} onChange={set("daysInPayPeriod")} data-testid="pkg-days" />
          </Field>
        )}
        <Field label="Basic pay for the last full pay period before notice (£)" hint="Before tax, leaving out overtime, bonus, commission and allowances." htmlFor="pkg-bp">
          <input id="pkg-bp" type="number" min="0" step="0.01" inputMode="decimal" className={inputClass} value={form.basicPayLastPeriod} onChange={set("basicPayLastPeriod")} data-testid="pkg-bp" />
        </Field>
      </div>

      {r?.error && <p className="mt-6 text-sm font-semibold text-signal">{r.error}</p>}
      {r && !r.error && (
        <Result
          items={[
            { label: "Tax-free", value: gbp(r.taxFree), big: true, sub: `within the ${gbp(TERMINATION_TAX_FREE.amount)} threshold` },
            { label: "Taxed as earnings", value: gbp(r.earnings), sub: r.penp > 0 ? `includes ${gbp(r.penp)} post-employment notice pay` : "income tax and National Insurance" },
            { label: "Taxable over £30,000", value: gbp(r.taxableExcess), sub: r.taxableExcess > 0 ? `income tax only; employer Class 1A ${gbp(r.employerClass1A)}` : "nothing over the threshold" },
            { label: "Total package", value: gbp(r.total) },
          ]}
          notes={notes}
        />
      )}
    </CalculatorShell>
  );
}
